import React from 'react'
import { useGetAgencyByAgencyId } from '../../../api/agencies/useGetAgencyByAgencyId'
import { Outlet, useParams } from 'react-router'
import { Button } from '@demandlocal/components'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../../store/selectors/userSelector'
import Error404 from '../../../components/404'
import { useModal } from '../../../components/useModal'
import AdminEditAgency from '../../../forms/Admin/AdminEditAgency'
import { ReactComponent as EditIcon } from './../../../images/icons/action_edit.svg'
import { useCreateBillingPortalSession } from '../../../api/agencies/payments/useCreateBillingPortalSession'
import PermissionGate from '../../../components/PermissionGate/PermissionGate'
import PageHeader from '../../../components/PageHeader/PageHeader'
import { ReactComponent as AgencyIcon } from '../../../images/icons/agency_icon.svg'

const AgencyTemplate = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const { agency_id } = useParams()
  const user = useSelector(currentUserSelector)
  const { data: agency } = useGetAgencyByAgencyId(agency_id)
  const { handleModal } = useModal()
  const {
    mutate: createBillingPortalSession,
    isLoading: isCreatingBillingPortalSession,
  } = useCreateBillingPortalSession()

  return (
    <div className={'flex flex-1 flex-col h-full '}>
      <div className="print:hidden">
        <PageHeader
          tabMenu={[
            { name: 'Overview', to: 'overview' },
            { name: 'Clients', to: 'clients' },
            { name: 'Campaigns', to: 'campaigns' },
            ...((user?.role_id ?? 3) <= 2 ? [{ name: 'Users', to: 'users' }] : []),
            { name: 'Invoices', to: 'invoices' },
            { name: 'Reports', to: 'reports', visible: user?.role_id === 1 },
            { name: 'Exports', to: 'exports' },
            ...(user?.role_id === 1
              ? [
                  { name: 'Invoice History', to: 'invoice-history' },
                  { name: 'Invoice Details', to: 'invoice-details' },
                ]
              : []),
          ]}
          title={agency?.name}
          icon={<AgencyIcon className="w-10 h-10" />}
          infoPill={{ type: 'warning', content: 'AGENCY' }}
          rightSlot={({ hidden }) =>
            user?.role_id === 1 ? (
              <div className={'flex flex-row gap-2'}>
                {agency?.stripe_enabled ? (
                  <Button
                    disabled={isCreatingBillingPortalSession}
                    variant={'rainbow'}
                    onClick={(e) => {
                      e.preventDefault()
                      createBillingPortalSession(
                        { agency_id: agency_id ?? 0 },
                        {
                          onSuccess: (data) => {
                            window.location.href = data.url
                          },
                        },
                      )
                    }}>
                    Billing Portal
                  </Button>
                ) : (
                  <></>
                )}
                <Button
                  leftSlot={<EditIcon stroke={'black'} />}
                  collapsed={hidden}
                  onClick={(e) => {
                    e.preventDefault()
                    handleModal(<AdminEditAgency agency={agency} />, { maxWidth: 1024 })
                  }}>
                  Edit Agency
                </Button>
              </div>
            ) : agency?.stripe_enabled ? (
              <PermissionGate requiredUserClaims={['can_access_billing']}>
                <Button
                  disabled={isCreatingBillingPortalSession}
                  variant={'rainbow'}
                  onClick={(e) => {
                    e.preventDefault()
                    createBillingPortalSession(
                      { agency_id: agency_id ?? 0 },
                      {
                        onSuccess: (data) => {
                          window.location.href = data.url
                        },
                      },
                    )
                  }}>
                  Billing Portal
                </Button>
              </PermissionGate>
            ) : (
              <></>
            )
          }
        />
      </div>
      <div className={'flex flex-1 flex-col p-10 max-w-[1920px] mx-auto w-full'}>
        {children}
      </div>
    </div>
  )
}

const AgencyProfile = (): JSX.Element => {
  const user = useSelector(currentUserSelector)
  if ((user?.role_id ?? 3) > 2) return <Error404 />

  return (
    <AgencyTemplate>
      <div className={'flex flex-1 flex-col gap-12'}>
        <Outlet />
      </div>
    </AgencyTemplate>
  )
}

export default AgencyProfile
